import React, { useState } from "react";
import {
  Space,
  Typography,
  Card,
  Descriptions,
  Button,
  Tag,
  message,
  Divider,
  Alert,
  Spin,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getQbStatus } from "../../api/quickbooks";
import { refreshData } from "../../api/dashboard";
import ConnectButton from "../quickbooks/ConnectButton";
import type { QbStatus, AppSettings } from "../../types/dashboard";

const { Title, Text } = Typography;

// ─── Hard-coded display values (real values come from backend settings API) ───
// This component displays read-only environment info. Editing is out of scope.

const PLACEHOLDER_SETTINGS: AppSettings = {
  qbEnvironment: "sandbox",
  qbRealmId: "—",
  qbClientIdMasked: "—",
  emailWhitelist: [],
  entraTenantId: "—",
  entraClientIdMasked: "—",
};

function formatDateFr(isoDate: string | null): string {
  if (!isoDate) return "—";
  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(isoDate));
}

function daysUntil(isoDate: string | null): number | null {
  if (!isoDate) return null;
  return Math.ceil(
    (new Date(isoDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
}

/**
 * Settings page: QuickBooks connection details, environment toggle (display),
 * email whitelist, and manual data refresh.
 */
export default function SettingsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const {
    data: qbStatus,
    isLoading: statusLoading,
    error: statusError,
  } = useQuery<QbStatus, Error>({
    queryKey: ["qbStatus"],
    queryFn: getQbStatus,
    staleTime: 60 * 1000,
    retry: 1,
  });

  // In a full implementation this would call a /api/settings endpoint.
  // For now we show placeholder values that indicate "not yet configured".
  const settings: AppSettings = PLACEHOLDER_SETTINGS;

  async function handleRefresh(): Promise<void> {
    setRefreshing(true);
    try {
      await refreshData();
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["classes"] });
      void message.success("Données rafraîchies avec succès.");
    } catch {
      void message.error("Échec du rafraîchissement des données.");
    } finally {
      setRefreshing(false);
    }
  }

  const tokenDays = daysUntil(qbStatus?.tokenExpiry ?? null);
  const refreshDays = daysUntil(qbStatus?.refreshTokenExpiry ?? null);

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: "100%", display: "flex" }}
    >
      <Title level={3} style={{ marginBottom: 0 }}>
        Paramètres
      </Title>

      {/* ── QuickBooks connection ────────────────────────────── */}
      <Card title="Connexion QuickBooks">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <ConnectButton />

          <Divider style={{ margin: "12px 0" }} />

          {statusLoading && <Spin size="small" />}

          {statusError && (
            <Alert
              type="error"
              message="Impossible de récupérer le statut QuickBooks."
              showIcon
            />
          )}

          {qbStatus && !statusLoading && (
            <Descriptions
              column={{ xs: 1, sm: 2 }}
              size="small"
              bordered
            >
              <Descriptions.Item label="Statut">
                {qbStatus.connected ? (
                  <Tag color="success">Connecté</Tag>
                ) : (
                  <Tag color="error">Déconnecté</Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Realm ID">
                <Text copyable={!!qbStatus.realmId}>
                  {qbStatus.realmId ?? "—"}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Expiration du jeton d'accès">
                {tokenDays !== null ? (
                  <Text type={tokenDays <= 1 ? "danger" : "secondary"}>
                    {formatDateFr(qbStatus.tokenExpiry)}{" "}
                    {tokenDays > 0
                      ? `(dans ${tokenDays} jour(s))`
                      : "(expiré)"}
                  </Text>
                ) : (
                  "—"
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Expiration du jeton de rafraîchissement">
                {refreshDays !== null ? (
                  <Text type={refreshDays <= 14 ? "warning" : "secondary"}>
                    {formatDateFr(qbStatus.refreshTokenExpiry)}{" "}
                    {refreshDays > 0
                      ? `(dans ${refreshDays} jour(s))`
                      : "(expiré)"}
                  </Text>
                ) : (
                  "—"
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Dernier rafraîchissement des données" span={2}>
                {formatDateFr(qbStatus.lastRefresh)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Space>
      </Card>

      {/* ── Environment ──────────────────────────────────────── */}
      <Card title="Environnement QuickBooks">
        <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
          <Descriptions.Item label="Environnement actuel">
            <Tag color={settings.qbEnvironment === "production" ? "blue" : "orange"}>
              {settings.qbEnvironment === "production"
                ? "Production"
                : "Sandbox"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Client ID (masqué)">
            {settings.qbClientIdMasked}
          </Descriptions.Item>
        </Descriptions>
        <Text
          type="secondary"
          style={{ display: "block", marginTop: 12, fontSize: 12 }}
        >
          La modification de l'environnement nécessite une mise à jour des
          variables d'environnement du serveur.
        </Text>
      </Card>

      {/* ── Email whitelist ──────────────────────────────────── */}
      <Card title="Liste blanche d'e-mails">
        {settings.emailWhitelist.length === 0 ? (
          <Text type="secondary">Aucune adresse configurée.</Text>
        ) : (
          <Space wrap>
            {settings.emailWhitelist.map((email) => (
              <Tag key={email}>{email}</Tag>
            ))}
          </Space>
        )}
        <Text
          type="secondary"
          style={{ display: "block", marginTop: 12, fontSize: 12 }}
        >
          La liste blanche est gérée via les variables d'environnement du
          serveur (EMAIL_WHITELIST).
        </Text>
      </Card>

      {/* ── Data refresh ─────────────────────────────────────── */}
      <Card title="Données">
        <Space direction="vertical">
          <Text type="secondary">
            Déclenche une synchronisation manuelle depuis QuickBooks et
            invalide le cache du tableau de bord.
          </Text>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={() => void handleRefresh()}
          >
            Rafraîchir les données
          </Button>
        </Space>
      </Card>
    </Space>
  );
}
